// =============================================================================
// PLATFORM ALGORITHM RULES — the shared, cross-user "how each platform ranks you"
// lake (the third sibling beside public_events + industry_vocab).
//
// Researched ONCE (the 70-slice research run, 2026-06-18) and reused by everyone:
// the concrete ranking / parsing / discoverability mechanics of LinkedIn, Seek,
// Indeed, ATS systems, etc., plus the cross-platform universals. Deliberately NO
// userId. The Platform Optimizer reads these to tune each profile per platform,
// truthfully, replacing the hardcoded PLATFORM RULES the PresenceEngine carried.
//
// Keyed by ruleKey (a slug) so a rule is stored exactly once and re-research
// upserts it (ON CONFLICT (rule_key)). verifyState + lastVerifiedAt feed the same
// staleness banding as the other lakes; 'seed' rules harden to 'researched' once an
// Exa-backed pass confirms them.
// =============================================================================

import { sql } from 'drizzle-orm';
import { index, integer, jsonb, pgTable, timestamp, varchar } from 'drizzle-orm/pg-core';
import { id, timestamps } from './_shared';

/** Provenance for a rule: every directive traces to at least one source. */
export interface PlatformRuleSource {
  url: string;
  title?: string;
  retrievedAt?: string; // ISO, stamped at research time
}

export const platformAlgorithmRules = pgTable(
  'platform_algorithm_rules',
  {
    id: id(),
    // Dedup key: slug of platform + rule, e.g. 'linkedin-exact-title-alignment'.
    // UNIQUE enforces research-once + lets re-research upsert in place.
    ruleKey: varchar('rule_key', { length: 200 }).notNull().unique(),
    // Platform bucket: 'linkedin' | 'seek' | 'indeed' | 'glassdoor' | 'wellfound' |
    // 'personal-seo' | 'github' | 'ats:workday' | 'ats:greenhouse' | 'ats:lever' |
    // 'cross-platform' (the universals applied everywhere).
    platform: varchar('platform', { length: 40 }).notNull(),
    // One-line statement of the mechanic.
    rule: varchar('rule', { length: 400 }).notNull(),
    // The actionable optimisation directive(s) a profile/CV optimiser applies.
    directive: jsonb('directive').$type<string[]>().notNull(),
    // 'high' | 'medium' | 'low' — leverage.
    importance: varchar('importance', { length: 8 }).notNull().default('medium'),
    // 'high' | 'medium' | 'low' — evidence strength (platforms hide their algorithms).
    ruleConfidence: varchar('rule_confidence', { length: 8 }).notNull().default('medium'),
    // 1 = sources disagree on this rule; surfaced, never silently resolved.
    contested: integer('contested').notNull().default(0),
    // Provenance: every rule traces here. Empty = unverified seed.
    sources: jsonb('sources').$type<PlatformRuleSource[]>().default(sql`'[]'::jsonb`),
    // 'seed' | 'researched' | 'unverified' | 'stale'.
    verifyState: varchar('verify_state', { length: 16 }).notNull().default('seed'),
    lastVerifiedAt: timestamp('last_verified_at', { withTimezone: true }),
    // Which research pass last touched it (e.g. 'research-2026-06-18', 'exa-pass').
    researchedBy: varchar('researched_by', { length: 64 }),
    // Optional 0-100 numeric confidence, mirroring the other lakes.
    confidence: integer('confidence'),
    ...timestamps,
  },
  (t) => ({
    keyIdx: index('platform_algorithm_rules_key_idx').on(t.ruleKey),
    platformIdx: index('platform_algorithm_rules_platform_idx').on(t.platform),
  }),
);
